import { createStore, createEffect, createEvent, combine } from 'effector';
import i18n from 'i18n';
import { $notificationStore } from '../notificationStore';
import { ApiUtils } from '@shared/utils';
import { CardsApi } from '@shared/api/rest';
import {
  CardStatus,
  type CardDetailsResponse,
  type RemindPinRequest,
  type ReportLostStolenRequest,
  type UpdateCategoryCardRequest,
  type UpdateLabelCardRequest,
} from '@shared/api/rest/cards';

import { type IQueryParams, RequestStrategyEnum } from '@shared/api/models';

const getCardEffect = createEffect((params: IQueryParams<number>) => CardsApi.getCard(params.request, params?.signal));
const activateEffect = createEffect((cardId: number) => CardsApi.activate(cardId));
const updateLabelCardEffect = createEffect((request: UpdateLabelCardRequest) => CardsApi.updateLabelCard(request));
const updateCategoryCardEffect = createEffect((request: UpdateCategoryCardRequest) => CardsApi.updateCategoryCard(request));
const remindPinEffect = createEffect((request: RemindPinRequest) => CardsApi.remindPin(request));
const freezeCardEffect = createEffect((cardId: number) => CardsApi.freezeCard(cardId));
const unfreezeCardEffect = createEffect((cardId: number) => CardsApi.unFreezeCard(cardId));
const reportLostStolenEffect = createEffect((request: ReportLostStolenRequest) => CardsApi.reportLostStolen(request));

const clearEvent = createEvent();

const $dataStore = createStore<CardDetailsResponse | null>(null)
  .on(getCardEffect.done, (current, data) => data.result.data || current)
  .on(freezeCardEffect.done, (current, data) => (data.result.data ? { ...current!, status: data.result.data.status } : current))
  .on(unfreezeCardEffect.done, (current, data) => (data.result.data ? { ...current!, status: data.result.data.status } : current))
  .on(updateLabelCardEffect.done, (current, data) => data.result.data || current)
  .on(updateCategoryCardEffect.done, (current, data) => data.result.data || current)
  .on(activateEffect.done, (current, data) => (data.result.data ? { ...current!, status: CardStatus.ACTIVE } : current))
  .reset(clearEvent);

const $loadingStore = createStore<boolean>(false)
  .on(getCardEffect.pending, (_, data) => data)
  .on(getCardEffect.fail, () => false)
  .on(getCardEffect.done, () => false)
  .reset(clearEvent);
const $actionLoadingStore = createStore<boolean>(false)
  .on(activateEffect.pending, (_, data) => data)
  .on(activateEffect.fail, () => false)
  .on(activateEffect.done, () => false)
  .on(updateLabelCardEffect.pending, (_, data) => data)
  .on(updateLabelCardEffect.fail, () => false)
  .on(updateLabelCardEffect.done, () => false)
  .on(updateCategoryCardEffect.pending, (_, data) => data)
  .on(updateCategoryCardEffect.fail, () => false)
  .on(updateCategoryCardEffect.done, () => false)
  .on(remindPinEffect.pending, (_, data) => data)
  .on(remindPinEffect.fail, () => false)
  .on(remindPinEffect.done, () => false)
  .on(freezeCardEffect.pending, (_, data) => data)
  .on(freezeCardEffect.fail, () => false)
  .on(freezeCardEffect.done, () => false)
  .on(unfreezeCardEffect.pending, (_, data) => data)
  .on(unfreezeCardEffect.fail, () => false)
  .on(unfreezeCardEffect.done, () => false)
  .on(reportLostStolenEffect.pending, (_, data) => data)
  .on(reportLostStolenEffect.fail, () => false)
  .on(reportLostStolenEffect.done, () => false)
  .reset(clearEvent);

const $errorStore = createStore<string | null>(null)
  .on(getCardEffect, () => null)
  .on(getCardEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(getCardEffect.done, () => null)
  .on(freezeCardEffect, () => null)
  .on(freezeCardEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(freezeCardEffect.done, () => null)
  .on(unfreezeCardEffect, () => null)
  .on(unfreezeCardEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(unfreezeCardEffect.done, () => null)
  .on(remindPinEffect, () => null)
  .on(remindPinEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(remindPinEffect.done, () => null)
  .on(updateLabelCardEffect, () => null)
  .on(updateLabelCardEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(updateLabelCardEffect.done, () => null)
  .on(updateCategoryCardEffect, () => null)
  .on(updateCategoryCardEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(updateCategoryCardEffect.done, () => null)
  .on(reportLostStolenEffect, () => null)
  .on(reportLostStolenEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(reportLostStolenEffect.done, () => null)
  .on(activateEffect, () => null)
  .on(activateEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(activateEffect.done, () => null)
  .reset(clearEvent);
$errorStore.watch((x) => ApiUtils.errorStoreActionForbiddenWatcher(x, $notificationStore.events.setError));

export const $cardStore = {
  events: {
    getCard: ApiUtils.wrapperApi(getCardEffect, RequestStrategyEnum.Debounce),
    freezeCard: freezeCardEffect,
    unfreezeCard: unfreezeCardEffect,
    remindPin: remindPinEffect,
    updateLabelCard: updateLabelCardEffect,
    updateCategoryCard: updateCategoryCardEffect,
    reportLostStolen: reportLostStolenEffect,
    activate: activateEffect,
    clear: clearEvent,
  },
  store: combine({
    data: $dataStore,
    loading: $loadingStore,
    actionLoading: $actionLoadingStore,
    error: $errorStore,
  }),
};
